import React, { useState, useEffect } from "react";
import AskedOfYou from "../components/AskedOfYou.jsx";
import Wordmark from "../components/Wordmark.jsx";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";
import UserChip from "../components/UserChip.jsx";

/**
 * ContributorHome — the landing for anyone invited to help, in either account.
 *
 * One page for two arrivals that look different on paper and identical in
 * practice:
 *
 *   Ruth, invited by her daughter into a family account.
 *   Ada, invited by a coordinator into an agency's account.
 *
 * Both were asked because they know the person. Both can add and not
 * restructure. Neither cares which kind of account they landed in, and neither
 * should be shown a page built around a caseload or a household.
 *
 * Before this existed, Ada was labelled "caregiver" and sent to a read-only
 * list with nowhere to contribute — invited to help, then given no way to.
 */
export default function ContributorHome() {
  const { user, logout, apiFetch } = useAuth();
  const navigate = useNavigate();
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);

  async function load() {
    try {
      // Same endpoint the family dashboard uses: it returns the clients the
      // caller may view within their own account, whichever kind that is.
      const r = await apiFetch("/api/family/clients");
      if (!r.ok) throw new Error("We couldn't load this just now.");
      setData(await r.json());
    } catch (e) {
      if (!e.sessionEnded) setError(e.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  const first   = user?.display_name?.split(" ")[0] || "there";
  const clients = data?.clients || [];

  return (
    <div style={s.page}>
      <div style={s.bar}>
        <Wordmark />
        <UserChip user={user} onLogout={handleLogout} />
      </div>

      <div style={s.container}>
        <h1 style={s.heading}>Thank you, {first}</h1>
        <AskedOfYou />
        <p style={s.sub}>
          {clients.length === 1
            ? `Someone asked you to help with ${clients[0].display_name}'s profile, because you know them. Anything you add helps the people caring for them know them too.`
            : "You've been asked to help with these profiles, because you know these people."}
        </p>

        {error && <p style={s.err}>{error}</p>}
        {!data && !error && <p style={s.muted}>Loading…</p>}

        {data && clients.length === 0 && (
          <div style={s.empty}>
            <p style={s.emptyTitle}>Nothing here yet</p>
            <p style={s.muted}>
              When someone shares a profile with you, it'll appear here.
            </p>
          </div>
        )}

        <div style={s.grid}>
          {clients.map(c => {
            const caps = c.capabilities || [];
            const canContribute = caps.includes("contribute_client");
            const meta = [c.age, c.location].filter(Boolean).join(" · ");
            return (
              <div key={c.id} style={s.card}>
                <div style={s.cardHead}>
                  <div style={s.avatar}>{c.display_name[0]}</div>
                  <div>
                    <div style={s.name}>{c.display_name}</div>
                    {meta && <div style={s.meta}>{meta}</div>}
                  </div>
                </div>

                {c.hero_oneliner && <p style={s.body}>{c.hero_oneliner}</p>}

                {canContribute ? (
                  <div style={s.actions}>
                    <Link to={`/coordinator/clients/${c.id}/interviews`} style={s.primaryBtn}>
                      Answer questions
                    </Link>
                    <Link to={`/family/clients/${c.id}/photos`} style={s.primaryBtn}>
                      Add photos
                    </Link>
                    <Link to={`/family/clients/${c.id}/documents`} style={s.primaryBtn}>
                      Add documents
                    </Link>
                  </div>
                ) : (
                  <Link to={`/clients/${c.id}`} style={s.primaryBtn}>
                    See the profile
                  </Link>
                )}

                <Link to={`/clients/${c.id}`} style={s.secondaryLink}>
                  See what's there so far →
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const s = {
  page:       { minHeight: "100dvh", background: "var(--color-bg-subtle)" },
  bar:        { background: "var(--color-surface)", borderBottom: "1px solid var(--color-border)", padding: "12px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  wordmark:   { fontWeight: "var(--weight-medium)", fontSize: 17, letterSpacing: "-0.3px", color: "var(--color-text-primary)", display: "flex", alignItems: "center", gap: 6 },
  dot:        { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--color-accent)" },
  container:  { maxWidth: 760, margin: "0 auto", padding: "48px 32px 80px" },
  heading:    { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-medium)", letterSpacing: "-0.5px", color: "var(--color-text-primary)" },
  sub:        { fontSize: "var(--text-base)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: "10px 0 32px", maxWidth: 560 },
  grid:       { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 18 },
  card:       { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: 26, display: "flex", flexDirection: "column", gap: 14 },
  cardHead:   { display: "flex", alignItems: "center", gap: 16 },
  avatar:     { width: 56, height: 56, borderRadius: "50%", background: "var(--color-accent-subtle)", color: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-medium)", fontSize: 20, flexShrink: 0 },
  name:       { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  meta:       { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 3 },
  body:       { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: 0, paddingTop: 12, borderTop: "1px solid var(--color-border)" },
  actions:    { display: "flex", gap: 8 },
  primaryBtn: { flex: 1, textAlign: "center", background: "var(--color-accent)", color: "#fff", textDecoration: "none", borderRadius: "var(--radius-md)", padding: "11px 14px", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)" },
  secondaryLink: { fontSize: "var(--text-sm)", color: "var(--color-accent)", textDecoration: "none" },
  empty:      { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: "40px 32px", textAlign: "center" },
  emptyTitle: { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", marginBottom: 6 },
  muted:      { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)" },
  err:        { fontSize: "var(--text-sm)", color: "var(--color-critical)" },
};
